import { Injectable } from "@angular/core";
import { ModalController } from "@ionic/angular";
import { LocationStateQuery } from "../state/location-state.query";
import { ClosingTimeReminderModalService } from "../../modals/closing-time-reminder/closing-time-reminder-modal.service";
import { ClosingTimeReminderComponent } from "../../modals/closing-time-reminder/closing-time-reminder.component";

@Injectable({ providedIn: "root" })
export class ClosingTimeCheckService {
	constructor(
		private locationStateQuery: LocationStateQuery,
		private modalController: ModalController,
		private closingTimeReminderModalService: ClosingTimeReminderModalService,
	) {}

	private readonly THRESHOLD_HOURS = 1;
	private readonly GRACE_MINUTES = 5;
	private readonly STORAGE_KEY = "closing-time-reminder-last-shown";

	private getLastShownTimestamp(): number | null {
		try {
			const stored = localStorage.getItem(this.STORAGE_KEY);
			if (!stored) {
				return null;
			}
			const timestamp = parseInt(stored, 10);
			return isNaN(timestamp) ? null : timestamp;
		} catch (error) {
			console.warn("[ClosingTimeCheck] Failed to read last shown timestamp from localStorage", error);
			return null;
		}
	}

	private setLastShownTimestamp(timestamp: number): void {
		try {
			localStorage.setItem(this.STORAGE_KEY, timestamp.toString());
		} catch (error) {
			console.warn("[ClosingTimeCheck] Failed to save last shown timestamp to localStorage", error);
		}
	}

	async checkAndTriggerClosingTimeReminder(): Promise<void> {
		const state = this.locationStateQuery.getSnapshot();

		if (!state.rideOpsSettings?.closingTimeReminderEnabled) {
			return;
		}

		if (state.state !== "Open") {
			return;
		}

		if (!state.closingTime) {
			return;
		}

		const topModal = await this.modalController.getTop();
		if (topModal?.id === ClosingTimeReminderComponent.modalId) {
			return;
		}

		const lastShown = this.getLastShownTimestamp();
		const now = Date.now();
		if (lastShown !== null) {
			const hoursSinceLastShow = (now - lastShown) / (1000 * 60 * 60);
			if (hoursSinceLastShow < this.THRESHOLD_HOURS) {
				return;
			}
		}

		const closingTime = new Date(state.closingTime);
		closingTime.removeMinutes(this.GRACE_MINUTES);

		if (closingTime.getTime() <= now) {
			await this.closingTimeReminderModalService.show();
			this.setLastShownTimestamp(now);
		}
	}
}
